// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Styled-component primitives composed by `XngCarousel` to lay out slides.
 *
 * Layout hierarchy:
 *
 *     CarouselWrapper
 *       └─ CarouselContainer   ← Embla viewport (`ref`)
 *            └─ SlidesContainer ← Embla container (flex or grid)
 *                 └─ SlideItem  ← One per child
 */

import { SlidesPerView } from "@vanmeter/App/Components/XngCarousel/models";
import { getSlideWidth } from "@vanmeter/App/Components/XngCarousel/utils";
import { getSystemColor } from "@vanmeter/XDL/Utilities";
import styled, { css } from "styled-components";

/** Layout props shared by the carousel's styled-components. */
export interface CarouselLayoutProps {
    /** Number of grid rows (only when `view === "grid"`). */
    rows?: number;
    /** Vertical scroll direction. */
    vertical?: boolean;
    /** Layout mode for `SlidesContainer`. */
    view?: "flex" | "grid";
    /** Gap between slides in pixels. */
    spaceBetween: number;
    /** Whether the carousel is looping; affects padding for grid+loop. */
    loop?: boolean;
    /** How many slides are visible. See {@link SlidesPerView}. */
    slidesPerView?: SlidesPerView;
    /** Fixed height for vertical carousels (in pixels). */
    height?: string;
}

// ─── Wrapper ──────────────────────────────────────────────────────────────────

/** Positioning host for the carousel (handles the outer flex behavior). */
export const CarouselWrapper = styled.div`
    position: relative;
    flex: 1;
    min-width: 0;
    min-height: 0;
    width: 100%;
`;

// ─── Embla viewport ───────────────────────────────────────────────────────────

/**
 * Embla viewport. The Embla ref is attached here. `overflow: hidden` is what
 * clips the slides; the actual horizontal/vertical scrolling is performed by
 * transform on the inner `SlidesContainer`.
 */
export const CarouselContainer = styled.div<CarouselLayoutProps>`
    overflow: hidden;
    width: 100%;

    ${({ view, loop, spaceBetween }) =>
        view === "grid" &&
        loop &&
        css`
            padding-left: ${spaceBetween}px;
        `}

    ${({ vertical, height }) =>
        vertical &&
        css`
            display: flex;
            height: ${height};
        `}
`;

// ─── Embla container ──────────────────────────────────────────────────────────

/**
 * Embla container. In `flex` mode it is a flex row/column of `SlideItem`s.
 * In `grid` mode it is a CSS grid sized by `getSlideWidth("grid", ...)`.
 */
export const SlidesContainer = styled.div<CarouselLayoutProps>`
    column-gap: ${({ spaceBetween }) => `${spaceBetween}px`};

    ${({ view, loop, spaceBetween }) =>
        view === "grid" &&
        loop &&
        css`
            padding-left: ${spaceBetween}px;
            margin-left: -${spaceBetween}px;
        `}

    ${({ view, vertical, rows, slidesPerView, spaceBetween }) =>
        view === "flex"
            ? css`
                  display: flex;
                  flex-direction: ${vertical ? "column" : "row"};
                  ${!vertical &&
                  css`
                      align-items: stretch;
                  `}
              `
            : css`
                  display: grid;
                  grid-auto-flow: ${vertical ? "row" : "column"};
                  justify-content: start;
                  grid-template-rows: repeat(${rows}, auto);
                  ${vertical &&
                  css`
                      grid-auto-rows: auto;
                  `}
                  ${getSlideWidth("grid", slidesPerView, spaceBetween)}
              `}
`;

// ─── Slide item ───────────────────────────────────────────────────────────────

/** Single slide. Width is computed from `slidesPerView`. */
export const SlideItem = styled.div<CarouselLayoutProps & { inlineNavigation?: boolean }>`
    ${({ slidesPerView, spaceBetween }) => getSlideWidth("flex", slidesPerView, spaceBetween)}
    min-width: 0;
    height: ${({ inlineNavigation }) => (inlineNavigation ? "max-content" : "auto")};
    position: relative;
`;

// ─── Dot ───────────────────────────────────────────────────────────
/** Controls Dot Pagination. Purely visual — the parent Clickable is the button. */
export const Dot = styled.span<{ active: boolean }>`
    display: block;
    width: 10px;
    height: 10px;
    border-radius: 100px;
    border: 1px solid ${getSystemColor("common.ui4")};
    padding: 0;
    background: ${({ active }) => (active ? getSystemColor("common.ui16") : "transparent")};
    cursor: pointer;
    transition:
        width 0.25s ease,
        background 0.25s ease;
    &:hover {
        border-color: ${getSystemColor("primary")};
        background: ${getSystemColor("common.white")};
    }
`;
